import Autosuggest, { type AutosuggestProps } from '@cloudscape-design/components/autosuggest';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { mergeRegistry, type NavSubItem } from '../nav.config.js';

/**
 * Inline search input rendered in TopNavigation's `search` slot. Dropdown
 * suggestions attach below the input (no modal), grouped by nav section.
 * AWS-Console-style — the operator's muscle memory pattern.
 *
 * Cmd/Ctrl+K focuses the input (the modal palette was previously routed
 * here; the shortcut still works, it just brings the cursor to the header
 * instead of opening an overlay).
 */
export function HeaderSearch(): ReactNode {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const inputRef = useRef<AutosuggestProps.Ref | null>(null);

  // Build grouped options from the merged nav tree once per render (cheap —
  // registry lookup is in-memory).
  const groups = useMemo<AutosuggestProps.OptionGroup[]>(() => {
    const out: AutosuggestProps.OptionGroup[] = [];
    for (const g of mergeRegistry()) {
      const options: AutosuggestProps.Option[] = [];
      for (const s of g.items) {
        if (!s.available && !(s.subItems && s.subItems.length)) continue;
        options.push({
          value: s.href,
          label: s.label,
          // Show the section's own destination as the "root" option, then
          // its sub-items nested via labelTag / description below.
        });
        if (s.subItems) {
          for (const sub of s.subItems) {
            options.push({
              value: sub.href,
              label: sub.label,
              description: sub.href,
            });
          }
        }
      }
      if (options.length) out.push({ label: g.label, options });
    }
    return out;
  }, []);

  // Focus the input on Cmd/Ctrl+K. Preserves the muscle-memory the modal had.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const flatByValue = useMemo(() => {
    const map = new Map<string, NavSubItem>();
    for (const g of groups) {
      for (const o of g.options) {
        if (o.value) map.set(o.value, { id: o.value, label: o.label ?? o.value, href: o.value });
      }
    }
    return map;
  }, [groups]);

  return (
    <Autosuggest
      ref={inputRef}
      value={value}
      onChange={({ detail }) => setValue(detail.value)}
      onSelect={({ detail }) => {
        const dest = flatByValue.get(detail.value);
        if (dest) {
          setValue('');
          navigate(dest.href);
        }
      }}
      // Cloudscape filters options by matching against label + description
      // by default — no custom filter needed.
      options={groups}
      placeholder="Search  (⌘K)"
      empty="No matches"
      enteredTextLabel={(v) => `Go to “${v}”`}
      // Keep the dropdown open even when the input is empty so users can
      // browse the full section tree from the header — same discoverability
      // the sidebar gives, without eating layout width.
      filteringType="auto"
    />
  );
}
